/**
 * Nuora UpCart — Custom Cart Drawer
 * Standalone replacement for UpCart with Shopify Cart API
 * Namespace: nuora-upcart-
 */

(function () {
  'use strict';

  var SELECTORS = {
    drawer: '#nuora-upcart-drawer',
    backdrop: '#nuora-upcart-backdrop',
    body: '#nuora-upcart-body',
    itemsList: '#nuora-upcart-items',
    cartCount: '[data-nuora-upcart-count]',
    discountRow: '#nuora-upcart-discount-row',
    checkoutBtn: '#nuora-upcart-checkout-btn',
    closeBtn: '#nuora-upcart-close-btn',
    emptyState: '#nuora-upcart-empty',
    cartContent: '#nuora-upcart-content',
    timerDisplay: '#nuora-upcart-timer',
    announcementBar: '#nuora-upcart-announcement',
    footerSection: '#nuora-upcart-footer',
  };

  var cart = null;
  var variantCompareAtMap = {};
  var timerInterval = null;
  var timerSeconds = 5 * 60;

  // ─── Cart API ───────────────────────────────────────────────

  async function fetchCart() {
    try {
      var res = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
      });
      cart = await res.json();
      await fetchCompareAtPrices();
      return cart;
    } catch (err) {
      console.error('[nuora-upcart] Failed to fetch cart:', err);
      return null;
    }
  }

  async function fetchCompareAtPrices() {
    if (!cart || !cart.items || cart.items.length === 0) return;

    var handles = [];
    cart.items.forEach(function (item) {
      if (item.handle && handles.indexOf(item.handle) === -1) {
        handles.push(item.handle);
      }
    });

    var fetches = handles.map(async function (handle) {
      try {
        var res = await fetch('/products/' + handle + '.js', {
          credentials: 'same-origin',
        });
        var product = await res.json();
        if (product.variants) {
          product.variants.forEach(function (v) {
            if (v.compare_at_price && v.compare_at_price > 0) {
              variantCompareAtMap[v.id] = v.compare_at_price;
            }
          });
        }
      } catch (err) {
        console.error('[nuora-upcart] Failed to fetch product ' + handle + ':', err);
      }
    });

    await Promise.all(fetches);
  }

  var isUpdating = false;

  async function removeItem(lineIndex) {
    if (isUpdating) return;
    isUpdating = true;

    // Immediately remove from UI
    var list = document.querySelector(SELECTORS.itemsList);
    if (list) {
      var items = list.querySelectorAll('.nuora-upcart-item');
      if (items[lineIndex - 1]) {
        items[lineIndex - 1].style.display = 'none';
      }
    }

    try {
      var res = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: lineIndex, quantity: 0 }),
      });
      cart = await res.json();
      await fetchCompareAtPrices();
      render();
    } catch (err) {
      console.error('[nuora-upcart] Failed to remove item:', err);
    }

    isUpdating = false;
  }

  // ─── Money Formatting ──────────────────────────────────────

  function formatMoney(cents) {
    var amount = cents / 100;
    var currency = (cart && cart.currency) ? cart.currency : 'USD';
    var localeMap = {
      USD: 'en-US',
      CAD: 'en-CA',
      GBP: 'en-GB',
      EUR: 'de-DE',
      AUD: 'en-AU',
      NZD: 'en-NZ',
      NGN: 'en-NG',
      JPY: 'ja-JP',
      INR: 'en-IN',
      ZAR: 'en-ZA',
      BRL: 'pt-BR',
      MXN: 'es-MX',
      SGD: 'en-SG',
      HKD: 'en-HK',
      KRW: 'ko-KR',
      SEK: 'sv-SE',
      NOK: 'nb-NO',
      DKK: 'da-DK',
      CHF: 'de-CH',
      AED: 'ar-AE',
      MYR: 'ms-MY',
      PHP: 'en-PH',
      THB: 'th-TH',
      IDR: 'id-ID',
      TWD: 'zh-TW',
      PLN: 'pl-PL',
      ILS: 'he-IL',
      CLP: 'es-CL',
      COP: 'es-CO',
      ARS: 'es-AR',
      PEN: 'es-PE',
    };
    var locale = localeMap[currency] || 'en-US';
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
      }).format(amount);
    } catch (e) {
      return currency + ' ' + amount.toFixed(2);
    }
  }

  // ─── Variant Tag ───────────────────────────────────────────

  function getVariantTag(item) {
    return item.variant_title || null;
  }

  // ─── Savings Calculation ───────────────────────────────────

  function getItemCompareAtTotal(item) {
    var compareAt = variantCompareAtMap[item.variant_id];
    if (compareAt && compareAt > 0) {
      var compareAtTotal = compareAt * item.quantity;
      if (compareAtTotal > item.final_line_price) {
        return compareAtTotal;
      }
    }
    return null;
  }

  function getItemSavings(item) {
    var compareAt = getItemCompareAtTotal(item);
    if (compareAt && compareAt > item.final_line_price) {
      return compareAt - item.final_line_price;
    }
    return 0;
  }

  function getTotalSavings() {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (sum, item) {
      return sum + getItemSavings(item);
    }, 0);
  }

  // ─── Tag Icon SVG ─────────────────────────────────────────

  function tagIconSVG(size) {
    size = size || 12;
    return '<svg width="' + size + '" height="' + size + '" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M8.575 4.649a3.75 3.75 0 012.7-1.149h1.975a3.25 3.25 0 013.25 3.25v2.187a3.25 3.25 0 01-.996 2.34l-4.747 4.572a2.5 2.5 0 01-3.502-.033l-2.898-2.898a2.75 2.75 0 01-.036-3.852l4.254-4.417zm4.425 3.351a1 1 0 100-2 1 1 0 000 2z"/></svg>';
  }

  // ─── Trash Icon SVG ───────────────────────────────────────

  function trashIconSVG() {
    return '<svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M11.5 8.25a.75.75 0 01.75.75v4.25a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75z"/><path d="M9.25 9a.75.75 0 00-1.5 0v4.25a.75.75 0 001.5 0V9z"/><path fill-rule="evenodd" d="M7.25 5.25a2.75 2.75 0 015.5 0h3a.75.75 0 010 1.5h-.75v5.45c0 1.68 0 2.52-.327 3.162a3 3 0 01-1.311 1.311c-.642.327-1.482.327-3.162.327h-.4c-1.68 0-2.52 0-3.162-.327a3 3 0 01-1.311-1.311C5 14.72 5 13.88 5 12.2V6.75h-.75a.75.75 0 010-1.5h3zm1.5 0a1.25 1.25 0 112.5 0h-2.5zm-2.25 1.5h7v5.45c0 .865-.001 1.423-.036 1.848-.033.408-.09.559-.128.633a1.5 1.5 0 01-.655.655c-.074.038-.225.095-.633.128-.425.035-.983.036-1.848.036h-.4c-.865 0-1.423-.001-1.848-.036-.408-.033-.559-.09-.633-.128a1.5 1.5 0 01-.656-.655c-.037-.074-.094-.225-.127-.633-.035-.425-.036-.983-.036-1.848V6.75z"/></svg>';
  }

  // ─── Render Cart Items ────────────────────────────────────

  function renderItems() {
    var list = document.querySelector(SELECTORS.itemsList);
    if (!list || !cart) return;

    if (!cart.items || cart.items.length === 0) {
      list.innerHTML = '';
      return;
    }

    var html = '';
    cart.items.forEach(function (item, index) {
      var savings = getItemSavings(item);
      var compareAt = getItemCompareAtTotal(item);
      var tag = getVariantTag(item);
      var imgSrc = '';
      if (item.featured_image && item.featured_image.url) {
        imgSrc = item.featured_image.url + '&width=176';
      } else if (item.image) {
        imgSrc = item.image + '&width=176';
      }
      var productUrl = item.url || '#';
      var lineIndex = index + 1;

      html += '<li class="nuora-upcart-item">';
      html += '<div class="nuora-upcart-item__tile">';
      html += '<a href="' + productUrl + '" class="nuora-upcart-item__image-wrap">';
      if (imgSrc) {
        html += '<img src="' + imgSrc + '" alt="' + item.product_title + '" width="88" height="88" class="nuora-upcart-item__image" loading="lazy">';
      } else {
        html += '<div class="nuora-upcart-item__image-placeholder"></div>';
      }
      html += '</a>';
      html += '<div class="nuora-upcart-item__info">';
      html += '<div class="nuora-upcart-item__header">';
      html += '<a href="' + productUrl + '" class="nuora-upcart-item__title">' + item.product_title + '</a>';
      html += '<button type="button" class="nuora-upcart-item__remove" data-line-index="' + lineIndex + '" aria-label="Remove ' + item.product_title + '">' + trashIconSVG() + '</button>';
      html += '</div>';
      html += '<div class="nuora-upcart-item__pricing">';
      if (compareAt && compareAt > item.final_line_price) {
        html += '<span class="nuora-upcart-item__compare-price">' + formatMoney(compareAt) + '</span>';
      }
      html += '<span class="nuora-upcart-item__price">' + formatMoney(item.final_line_price) + '</span>';
      if (savings > 0) {
        html += '<span class="nuora-upcart-item__savings">(You saved ' + formatMoney(savings) + ')</span>';
      }
      html += '</div>';
      if (tag) {
        html += '<div class="nuora-upcart-item__tag-row">';
        html += '<span class="nuora-upcart-tag">' + tagIconSVG() + ' <span>' + tag + '</span></span>';
        html += '</div>';
      }
      html += '</div>';
      html += '</div>';
      html += '</li>';
    });

    list.innerHTML = html;
  }

  // ─── Render Footer ────────────────────────────────────────

  function renderFooter() {
    var discountRow = document.querySelector(SELECTORS.discountRow);
    var footer = document.querySelector(SELECTORS.footerSection);
    if (!discountRow || !footer) return;

    var totalSavings = getTotalSavings();

    if (!cart || !cart.items || cart.items.length === 0) {
      footer.style.display = 'none';
      return;
    }

    footer.style.display = '';

    if (totalSavings > 0) {
      var tags = [];
      cart.items.forEach(function (item) {
        var tag = getVariantTag(item);
        if (tag && tags.indexOf(tag) === -1) tags.push(tag);
      });

      var tagBadges = tags.map(function (t) {
        return '<span class="nuora-upcart-tag">' + tagIconSVG() + ' <span>' + t + '</span></span>';
      }).join(' ');

      discountRow.innerHTML =
        '<div class="nuora-upcart-discount__left">' +
          '<span class="nuora-upcart-discount__label">Discounts</span>' +
          '<div class="nuora-upcart-discount__tags">' + tagBadges + '</div>' +
        '</div>' +
        '<span class="nuora-upcart-discount__amount">- ' + formatMoney(totalSavings) + '</span>';
      discountRow.style.display = '';
    } else {
      discountRow.style.display = 'none';
    }

    var checkoutBtn = document.querySelector(SELECTORS.checkoutBtn);
    if (checkoutBtn) {
      checkoutBtn.href = '/checkout';
    }
  }

  // ─── Render Cart Count ────────────────────────────────────

  function renderCount() {
    var countEls = document.querySelectorAll(SELECTORS.cartCount);
    var count = cart ? cart.item_count : 0;
    countEls.forEach(function (el) {
      el.textContent = count;
    });

    var headerTitle = document.querySelector('#nuora-upcart-header-title');
    if (headerTitle) {
      headerTitle.textContent = 'Your Cart \u00B7 ' + count;
    }
  }

  // ─── Empty / Content Toggle ───────────────────────────────

  function renderEmptyState() {
    var empty = document.querySelector(SELECTORS.emptyState);
    var content = document.querySelector(SELECTORS.cartContent);
    var announcement = document.querySelector(SELECTORS.announcementBar);
    if (!empty || !content) return;

    var isEmpty = !cart || !cart.items || cart.items.length === 0;
    empty.style.display = isEmpty ? 'flex' : 'none';
    content.style.display = isEmpty ? 'none' : 'flex';
    if (announcement) {
      announcement.style.display = isEmpty ? 'none' : '';
    }
  }

  // ─── Timer ────────────────────────────────────────────────

  function startTimer() {
    var display = document.querySelector(SELECTORS.timerDisplay);
    if (!display) return;

    clearInterval(timerInterval);
    timerSeconds = 5 * 60;

    function tick() {
      var mins = Math.floor(timerSeconds / 60).toString().padStart(2, '0');
      var secs = (timerSeconds % 60).toString().padStart(2, '0');
      display.textContent = mins + ':' + secs;

      if (timerSeconds <= 0) {
        clearInterval(timerInterval);
        return;
      }
      timerSeconds--;
    }

    tick();
    timerInterval = setInterval(tick, 1000);
  }

  // ─── Main Render ──────────────────────────────────────────

  function render() {
    renderCount();
    renderItems();
    renderFooter();
    renderEmptyState();
  }

  // ─── Drawer Open / Close ──────────────────────────────────

  function openDrawer() {
    var drawer = document.querySelector(SELECTORS.drawer);
    var backdrop = document.querySelector(SELECTORS.backdrop);
    if (!drawer || !backdrop) return;

    drawer.classList.add('nuora-upcart-drawer--open');
    backdrop.classList.add('nuora-upcart-backdrop--active');
    document.body.classList.add('nuora-upcart-body-lock');
    startTimer();
  }

  function closeDrawer() {
    var drawer = document.querySelector(SELECTORS.drawer);
    var backdrop = document.querySelector(SELECTORS.backdrop);
    if (!drawer || !backdrop) return;

    drawer.classList.remove('nuora-upcart-drawer--open');
    backdrop.classList.remove('nuora-upcart-backdrop--active');
    document.body.classList.remove('nuora-upcart-body-lock');
  }

  async function refreshAndOpen() {
    await fetchCart();
    render();
    openDrawer();
  }

  // ─── Fetch / XHR Intercept ────────────────────────────────

  function installFetchIntercept() {
    var originalFetch = window.fetch;

    window.fetch = function () {
      var args = arguments;
      var result = originalFetch.apply(this, args);

      try {
        var url = typeof args[0] === 'string'
          ? args[0]
          : (args[0] instanceof Request ? args[0].url : '');

        if (/\/cart\/add/.test(url)) {
          result.then(function () {
            // Wait for any pending remove to finish
            function waitAndOpen() {
              if (isUpdating) {
                setTimeout(waitAndOpen, 100);
              } else {
                refreshAndOpen();
              }
            }
            setTimeout(waitAndOpen, 300);
          }).catch(function () {});
        }
      } catch (e) {}

      return result;
    };
  }

  function installXHRIntercept() {
    var originalOpen = XMLHttpRequest.prototype.open;
    var originalSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._nuoraUpcartUrl = url;
      return originalOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      if (
        this._nuoraUpcartUrl &&
        /\/cart\/add/.test(this._nuoraUpcartUrl)
      ) {
        this.addEventListener('load', function () {
          setTimeout(function () { refreshAndOpen(); }, 300);
        });
      }
      return originalSend.apply(this, arguments);
    };
  }

  // ─── Cart Icon Click Binding ──────────────────────────────

  function bindCartIconClicks() {
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest(
        'a[href="/cart"], a[href="/cart/"], .cart-icon-bubble, .header__icon--cart, [data-nuora-upcart-trigger]'
      );
      if (trigger) {
        e.preventDefault();
        e.stopPropagation();
        refreshAndOpen();
      }
    });
  }

  // ─── Bind Drawer Events ───────────────────────────────────

  function bindDrawerEvents() {
    // Close button
    document.addEventListener('click', function (e) {
      if (e.target.closest(SELECTORS.closeBtn)) {
        e.preventDefault();
        closeDrawer();
      }
    });

    // Backdrop
    document.addEventListener('click', function (e) {
      if (e.target.id === 'nuora-upcart-backdrop') {
        closeDrawer();
      }
    });

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });

    // Remove item — capture phase to fire before anything else
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.nuora-upcart-item__remove');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var lineIndex = parseInt(btn.getAttribute('data-line-index'), 10);
      console.log('[nuora-upcart] Remove clicked, line:', lineIndex);
      if (lineIndex) removeItem(lineIndex);
    }, true);
  }

  // ─── Init ─────────────────────────────────────────────────

  async function init() {
    if (document.readyState === 'loading') {
      await new Promise(function (r) {
        document.addEventListener('DOMContentLoaded', r);
      });
    }

    if (!document.querySelector(SELECTORS.drawer)) {
      console.warn('[nuora-upcart] Drawer markup not found.');
      return;
    }

    installFetchIntercept();
    installXHRIntercept();
    bindCartIconClicks();
    bindDrawerEvents();

    await fetchCart();
    render();

    console.log('[nuora-upcart] Initialized. Variant compare-at map:', variantCompareAtMap);
  }

  window.NuoraUpcart = {
    open: refreshAndOpen,
    close: closeDrawer,
    refresh: async function () {
      await fetchCart();
      render();
    },
  };

  init();
})();